import {
  IInvoiceRespSchema,
  InvoiceStatusEnum,
  InvoiceSummaryResp,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { parseDateAsUtc } from '@monetize/utils/core';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const toUtcMidnight = (date: Date) =>
  Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

const diffDaysSinceDueDate = (dueDate: string, now: Date): number =>
  Math.floor(
    (toUtcMidnight(now) - toUtcMidnight(parseDateAsUtc(dueDate))) / MS_PER_DAY,
  );

export const getInvoiceDaysPastDue = (
  dueDate?: Maybe<string>,
  now: Date = new Date(),
): number => {
  if (!dueDate) {
    return 0;
  }
  const days = diffDaysSinceDueDate(dueDate, now);
  return days > 0 ? days : 0;
};

export const getDaysOutstanding = (
  dueDate?: Maybe<string>,
  now: Date = new Date(),
): number => {
  if (!dueDate) {
    return 0;
  }
  const days = diffDaysSinceDueDate(dueDate, now);
  return days >= 0 ? days + 1 : 0;
};

export const isInvoicePastDue = (
  {
    status,
    dueDate,
  }: {
    status?: Maybe<InvoiceStatusEnum>;
    dueDate?: Maybe<string>;
  },
  now: Date = new Date(),
): boolean =>
  status === InvoiceStatusEnum.UNPAID &&
  getInvoiceDaysPastDue(dueDate, now) > 0;

export function isSummaryInvoiceResponse(
  invoice: IInvoiceRespSchema | InvoiceSummaryResp,
): invoice is InvoiceSummaryResp {
  return !(invoice as IInvoiceRespSchema).billingContact;
}

/**
 * Returns why an invoice can't be voided, or undefined if it can be.
 * Checked in order of relevance so only the most useful reason is shown.
 */
export function getVoidInvoiceDisabledReason(
  invoice: Pick<
    IInvoiceRespSchema | InvoiceSummaryResp,
    'status' | 'amountPaid' | 'amountWrittenOff' | 'amountCredited'
  >,
): string | undefined {
  if (invoice.status !== InvoiceStatusEnum.UNPAID) {
    return 'Only unpaid invoices can be voided.';
  }
  if ((invoice.amountPaid ?? 0) > 0) {
    return 'Invoices with a payment applied cannot be voided.';
  }
  if (invoice.amountWrittenOff > 0) {
    return 'Invoices that have been written off cannot be voided.';
  }
  if (invoice.amountCredited > 0) {
    return 'Invoices with a credit note applied cannot be voided.';
  }
  return undefined;
}

export const getTaxCalculationError = (
  taxError?: Maybe<string>,
  canUpdateTax: boolean = false,
): { message: string; reviewAction?: 'ADDRESS' | 'TAX-CONFIG' } => {
  switch (taxError) {
    case 'customerAddressCouldNotResolve':
      return {
        message:
          'Tax calculation error: Address is invalid, confirm the invoice shipping address.',
        reviewAction: 'ADDRESS',
      };
    case 'invalidToken':
      if (canUpdateTax) {
        return {
          message:
            'Tax calculation error: Authentication failure, confirm your API key.',
          reviewAction: 'TAX-CONFIG',
        };
      }
      return {
        message:
          'Tax calculation error: Authentication failure, please notify your administrator.',
      };
    case 'productExternalIdUnknown':
      return {
        message:
          'Tax calculation error: Product mapping not configured correctly with your tax provider.',
      };
    // For all other errors (and unknown errors) fallback to showing the user the actual message
    case 'currencyCodeNotSupported':
    case 'taxDateTooFarInFuture':
    case 'transactionFrozenForFiling':
    case 'transactionIdNotFound':
    default: {
      let reviewAction: 'ADDRESS' | 'TAX-CONFIG' | undefined;
      const normalizedTaxError = taxError?.toLowerCase() || '';
      /**
       * These are mostly here for Avalara - we only have the message and not the code,
       * so we make a best-attempt to guess the action based on the message.
       */
      if (
        ['address', 'postal code', 'province', 'country', 'zip'].some((token) =>
          normalizedTaxError.includes(token),
        )
      ) {
        reviewAction = 'ADDRESS';
      }
      if (
        ['authentication', 'authenticate'].some((token) =>
          normalizedTaxError.includes(token),
        )
      ) {
        reviewAction = 'ADDRESS';
      }
      return { message: `Tax calculation error: ${taxError}`, reviewAction };
    }
  }
};
